import * as React from "react";

/** Text & icon size steps, as multiples of the browser's own root font size.
 *  Everything in the app is rem-based, so scaling the root scales text,
 *  icons and spacing together - keep new UI in rem (no text-[Npx] or px
 *  widths) or it won't follow this setting. */
export const UI_SCALE_STEPS = [0.9, 1, 1.1, 1.2, 1.3] as const;
export const DEFAULT_UI_SCALE = 1;

export type ThemePref = "light" | "dark" | "system";

const THEME_KEY = "agent-theme";
const SCALE_KEY = "asmara-ui-scale";
const DARK_QUERY = "(prefers-color-scheme: dark)";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private window / blocked storage - the choice just won't persist.
  }
}

function toScale(raw: string | null): number | null {
  const n = Number(raw);
  return UI_SCALE_STEPS.includes(n as (typeof UI_SCALE_STEPS)[number]) ? n : null;
}

/** No stored value means System - which is what the app did before it
 *  stored anything (it followed prefers-color-scheme). */
function readTheme(): ThemePref {
  const saved = read(THEME_KEY);
  return saved === "light" || saved === "dark" || saved === "system" ? saved : "system";
}

function systemDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

function applyTheme(dark: boolean) {
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
}

function applyScale(scale: number) {
  const root = document.documentElement;
  // A percentage, not px, so the browser's own font-size setting still counts.
  root.style.fontSize = scale === DEFAULT_UI_SCALE ? "" : `${scale * 100}%`;
  // Read by tailwind.config.js's scaled breakpoints and by scaleMediaQuery.
  if (scale === DEFAULT_UI_SCALE) root.removeAttribute("data-ui-scale");
  else root.setAttribute("data-ui-scale", String(scale));
}

/** Multiplies every px width in a media query by the text-size step, so a JS
 *  breakpoint (useMediaQuery) flips at the same width as Tailwind's scaled
 *  sm/md/lg - see tailwind.config.js. Write a "below" edge as N - 0.02px
 *  (e.g. max-width: 767.98px), so it still meets Tailwind's min-width: N
 *  once both are scaled. */
export function scaleMediaQuery(query: string, scale: number): string {
  if (scale === DEFAULT_UI_SCALE) return query;
  return query.replace(/(\d+(?:\.\d+)?)px/g, (_, n) => `${Math.round(Number(n) * scale * 100) / 100}px`);
}

/** Applies the saved theme and size before React's first render, so neither
 *  flashes in (including on the login screen). */
export function applyStoredPreferences() {
  const theme = readTheme();
  applyTheme(theme === "system" ? systemDark() : theme === "dark");
  applyScale(toScale(read(SCALE_KEY)) ?? DEFAULT_UI_SCALE);
}

interface Preferences {
  theme: ThemePref;
  setTheme: (theme: ThemePref) => void;
  /** The resolved theme - System follows the OS live. */
  dark: boolean;
  uiScale: number;
  setUiScale: (scale: number) => void;
}

const PreferencesContext = React.createContext<Preferences | null>(null);

export function PreferencesProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = React.useState<ThemePref>(readTheme);
  const [osDark, setOsDark] = React.useState(systemDark);
  const [uiScale, setUiScaleState] = React.useState(
    () => toScale(read(SCALE_KEY)) ?? DEFAULT_UI_SCALE
  );

  React.useEffect(() => {
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => setOsDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const dark = theme === "system" ? osDark : theme === "dark";

  React.useEffect(() => {
    applyTheme(dark);
  }, [dark]);

  React.useEffect(() => {
    applyScale(uiScale);
  }, [uiScale]);

  const setTheme = React.useCallback((next: ThemePref) => {
    setThemeState(next);
    write(THEME_KEY, next);
  }, []);

  const setUiScale = React.useCallback((next: number) => {
    setUiScaleState(next);
    write(SCALE_KEY, String(next));
  }, []);

  const value = React.useMemo(
    () => ({ theme, setTheme, dark, uiScale, setUiScale }),
    [theme, setTheme, dark, uiScale, setUiScale]
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): Preferences {
  const ctx = React.useContext(PreferencesContext);
  if (!ctx) throw new Error("usePreferences must be used inside PreferencesProvider");
  return ctx;
}
